
import React from 'react';
import Layout from '@/components/Layout';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar as CalendarIcon, BookOpen, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import JourneyGrid from '@/components/JourneyGrid';

const Guide: React.FC = () => {
  const { user, userProgress, isAuthenticated, isLoading } = useUser();
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <p className="text-center">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return null;
  }

  // Calculate lessons completed from user progress
  const lessonsCompleted = userProgress.completedDays?.length || 0;
  const totalDays = 30; // Assuming 30-day program

  return (
    <Layout>
      <div className="container py-8 max-w-5xl mx-auto px-4">
        <DashboardHeader 
          title="Hormone Health Guide" 
          description="Your personalized 30-day journey to better hormone health"
          showBackButton={true}
        />
        
        <Tabs defaultValue="journey" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="journey" className="flex items-center gap-2">
              <Map className="h-4 w-4" />
              <span>Journey Overview</span>
            </TabsTrigger>
            <TabsTrigger value="lessons" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span>Daily Lessons</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>Calendar View</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="journey" className="space-y-6">
            <Card className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <div>
                  <h2 className="text-xl font-medium mb-2">Your 30-Day Journey</h2>
                  <p className="text-muted-foreground">
                    Progress: {lessonsCompleted} of {totalDays} days completed
                  </p>
                </div>
                <Button 
                  onClick={() => navigate(`/lesson/${userProgress.currentDay || 1}`)}
                  className="mt-4 md:mt-0"
                >
                  Continue to Day {userProgress.currentDay || 1}
                </Button>
              </div>
              
              <div className="mt-4">
                <JourneyGrid />
              </div>
            </Card>
          </TabsContent>
          
          <TabsContent value="lessons">
            <Card className="p-6">
              <h2 className="text-xl font-medium mb-4">Daily Lessons</h2>
              <p className="text-muted-foreground mb-6">
                Access all your lessons and track your progress through the program.
              </p>
              
              {/* This would be replaced with your actual lesson list component */}
              <div className="grid gap-4">
                {[...Array(Math.min(userProgress.currentDay || 1, totalDays))].map((_, index) => (
                  <div key={index} className="flex justify-between items-center p-4 border rounded-lg">
                    <div>
                      <h3 className="font-medium">Day {index + 1}</h3>
                      <p className="text-sm text-muted-foreground">
                        {userProgress.completedDays?.includes(index + 1) ? 'Completed' : 'Not completed'}
                      </p>
                    </div>
                    <Button 
                      variant={userProgress.completedDays?.includes(index + 1) ? "outline" : "default"}
                      onClick={() => navigate(`/lesson/${index + 1}`)}
                      size="sm"
                    >
                      {userProgress.completedDays?.includes(index + 1) ? 'Review' : 'Start'}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
          
          <TabsContent value="calendar">
            <Card className="p-6">
              <h2 className="text-xl font-medium mb-4">Program Calendar</h2>
              <p className="text-muted-foreground mb-6">
                View your hormone health journey on a calendar to plan ahead.
              </p>
              
              {/* This would be replaced with your actual calendar component */}
              <div className="flex justify-center items-center h-64 border rounded-lg">
                <div className="text-center">
                  <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground/50" />
                  <p className="mt-4">Calendar view coming soon</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Guide;
